import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Combobox } from '@headlessui/react';
import useFetchDebouncedSearchSuggestions from '@th/frontend-shared/src/hooks/queries/useFetchDebouncedSearchSuggestions';
import PropertySearchBar from './PropertySearchBar';
import { PropertySnapshot } from '@th/shared/src/types/PropertySnapshot';
import { Place } from '@th/shared/src/types/Place';
import isPropertySnapshot from '@th/shared/src/types/typeGuards/isPropertySnapshot';
import isPlace from '@th/shared/src/types/typeGuards/isPlace';
import PropertySearchResults from './PropertySearchResults';
import { useSearch } from '../../contexts/SearchContext';
import useLazyFetchSearchFiltersByNlp from '@th/frontend-shared/src/hooks/queries/useLazyFetchSearchFiltersByNlp';
import useNavToPlace from '../../hooks/useNavToPlace';
import useFetchSearchGeneralAndNavToResult from '../../hooks/useFetchSearchGeneralAndNavToResult';
import navToListing from '../../helpers/navToListing';
import { useMap } from '../../contexts/MapContext';
import useFetchNlpSearchAndApplyFilters from '../../hooks/useFetchNlpSearchAndApplyFilters';
import useDebounce from '@th/frontend-shared/src/hooks/useDebounce';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

type Props = {};

export default function PropertySearch({}: Props) {
  const [searchPhrase, setSearchPhrase] = useState('');
  const debouncedSearchPhrase = useDebounce(searchPhrase);
  const {
    properties,
    places,
    isFetching: isSuggestionsFetching,
  } = useFetchDebouncedSearchSuggestions(searchPhrase);
  const {
    resetAndUpdateFiltersOnTopOfExistingCoreValues,
    clearGeoFilters,
    openNlpSearchStatusModalToLoading,
    closeNlpSearchModal,
  } = useSearch();
  const { clearAreaOverlay } = useMap();
  const [fetchSearchFiltersByNlp] = useLazyFetchSearchFiltersByNlp();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<Place | PropertySnapshot | string>();
  const navToPlace = useNavToPlace();

  return (
    <div className="w-full">
      <Combobox
        onChange={async (item: Place | PropertySnapshot | string) => {
          setSelected(item);
          if (item) {
            if (isPlace(item)) {
              clearGeoFilters();
              navToPlace(item);
            }
            if (isPropertySnapshot(item)) navToListing(item.listing);
            if (typeof item === 'string') {
              // item is an NLP search phrase
              const nlpPhrase = item;
              openNlpSearchStatusModalToLoading(nlpPhrase);
              try {
                const searchFilters = await fetchSearchFiltersByNlp(nlpPhrase);
                clearAreaOverlay();
                resetAndUpdateFiltersOnTopOfExistingCoreValues(searchFilters);
                closeNlpSearchModal();
              } catch (ex) {
                // TODO: Handle error
              }
            }
          }
        }}
        value={selected}
        nullable
      >
        {({ activeOption }) => (
          <>
            <PropertySearchBar
              ref={inputRef}
              searchPhrase={searchPhrase}
              onChangeSearchPhrase={setSearchPhrase}
              onChangeSelectedItem={setSelected}
              placeholder={'Search anything'}
              displayValueFormatter={(
                item: Place | PropertySnapshot | string,
              ) => {
                if (isPlace(item)) return item.name;
                if (isPropertySnapshot(item))
                  return item.listing.address.formattedAddressShortOneLiner();
                if (typeof item === 'string') return item;
                return '';
              }}
              activeOption={activeOption}
            />
            <PropertySearchResults
              searchPhrase={debouncedSearchPhrase}
              places={places}
              properties={properties}
              isSuggestionsFetching={isSuggestionsFetching}
            />
          </>
        )}
      </Combobox>
    </div>
  );
}
